// LogViewerPanel — Profile & Settings → Logs. Owner-only.
//
// WHY THIS EXISTS
// Reading production logs meant exporting them from the Vercel dashboard to a
// laptop, or running `gh run list` in a terminal. Both are outside the product and
// neither is available to the person who owns it. Everything here is read-only.
//
// THE GATE IS THE BACKEND, NOT THIS FILE
// ProfileSettingsPage hides the tab for non-owners as a courtesy. The real control
// is requireOwner on /api/admin/logs/* which 403s regardless of what renders. The
// same relationship as the Users tab — see the comment above ELEVATED_ROLES there.
//
// AND THE SCOPE IS ALSO THE BACKEND'S (P0.33, 25 Oct 2026). The activity source
// returns the caller's own tenant, or every tenant for SYSTEM_ADMIN_EMAILS; the
// response says which in `scope`. Nothing here filters, and nothing here should:
// a client-side filter over an all-tenant payload would still have shipped the
// other tenants' rows to the browser.
//
// WHY 403 IS HANDLED HERE
// src/lib/api.js fires onAuthFailure on 401 (which signs the user out) but
// deliberately NOT on 403: "authenticated but not permitted" is not a session
// problem. So a 403 arrives as an ordinary response and this panel must render it.

import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../lib/api';
import './LogViewerPanel.css';

const SOURCES = [
  { id: 'vercel',   label: 'Vercel',         hint: 'Runtime logs from the live production deployment.' },
  { id: 'github',   label: 'GitHub Actions', hint: 'Scheduled job history — the only record of cron runs.' },
  { id: 'activity', label: 'Activity',       hint: 'What the app recorded doing, from ai_sessions and import_runs.' },
];

const LEVELS = ['all', 'error', 'warn', 'info'];

function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const secs = Math.round((Date.now() - then) / 1000);
  if (secs < 60) return secs + 's ago';
  if (secs < 3600) return Math.round(secs / 60) + 'm ago';
  if (secs < 86400) return Math.round(secs / 3600) + 'h ago';
  return Math.round(secs / 86400) + 'd ago';
}

function fmtDuration(ms) {
  if (!ms && ms !== 0) return '';
  const s = Math.round(ms / 1000);
  return s < 60 ? s + 's' : Math.floor(s / 60) + 'm ' + (s % 60) + 's';
}

// Vercel says 'warning', the UI offers 'warn', and GitHub says 'failure'. One class
// so a red row is red whichever source produced it.
function toneOf(entry) {
  const v = String(entry.level || entry.conclusion || '').toLowerCase();
  if (['error', 'fatal', 'failure', 'timed_out'].includes(v)) return 'error';
  if (['warn', 'warning', 'cancelled', 'canceled'].includes(v)) return 'warn';
  if (['success', 'completed'].includes(v)) return 'ok';
  return 'info';
}

export default function LogViewerPanel() {
  const [source, setSource]   = useState('vercel');
  const [level, setLevel]     = useState('all');
  const [query, setQuery]     = useState('');
  const [data, setData]       = useState(null);   // null = not loaded yet
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (opts = {}) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (source === 'vercel') {
        if (level !== 'all') params.set('level', level);
        if (query.trim()) params.set('q', query.trim());
      }
      const qs = params.toString();
      const res = await apiFetch('/api/admin/logs/' + source + (qs ? '?' + qs : ''));
      const body = await res.json().catch(() => ({}));

      if (res.status === 403) {
        // Not a session failure — apiFetch deliberately leaves the user signed in.
        throw new Error(body.error || 'Owner access required to view logs.');
      }
      if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);

      setData(body);
    } catch (e) {
      setError(e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
    // opts is accepted so a manual Refresh reads as intentional at the call site.
    void opts;
  }, [source, level, query]);

  // Re-fetch when the source or level changes. The text filter is deliberately NOT
  // a dependency — it would fire a request per keystroke, the same unthrottled
  // typeahead problem logged as B49. Enter or Refresh applies it.
  useEffect(() => { load(); }, [source, level]);   // eslint-disable-line react-hooks/exhaustive-deps

  const entries = data?.entries || [];

  return (
    <div className="lv">
      <p className="lv-notice">
        <strong>Platform-wide.</strong> Vercel and GitHub Actions logs cover the whole
        deployment, not just your tenant, and can name other tenants&rsquo; accounts.
      </p>

      <div className="lv-tabs" role="tablist">
        {SOURCES.map(s => (
          <button
            key={s.id}
            role="tab"
            aria-selected={source === s.id}
            className={`lv-tab ${source === s.id ? 'lv-tab--active' : ''}`}
            onClick={() => setSource(s.id)}
          >
            {s.label}
          </button>
        ))}
      </div>

      <p className="lv-hint">{SOURCES.find(s => s.id === source)?.hint}</p>

      <div className="lv-controls">
        {source === 'vercel' && (
          <>
            <select
              className="lv-select"
              value={level}
              onChange={e => setLevel(e.target.value)}
              aria-label="Filter by level"
            >
              {LEVELS.map(l => <option key={l} value={l}>{l === 'all' ? 'All levels' : l}</option>)}
            </select>
            <input
              className="lv-search"
              type="search"
              placeholder="Filter text, then press Enter"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') load({ manual: true }); }}
              aria-label="Filter log text"
            />
          </>
        )}
        <button className="lv-refresh" onClick={() => load({ manual: true })} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
        {data?.cached && <span className="lv-cached" title="Served from a 60s server-side cache">cached</span>}
      </div>

      {error && <div className="lv-error">{error}</div>}

      {/* configured === false is not an error: the source simply has no credentials yet. */}
      {data && data.configured === false && (
        <div className="lv-empty">{data.note || 'This source is not configured.'}</div>
      )}

      {/* An upstream failure with the source configured — degraded, not broken. */}
      {data?.error && <div className="lv-error">{data.error}</div>}

      {data?.note && data.configured !== false && <div className="lv-note">{data.note}</div>}

      {!error && !loading && data && data.configured !== false && entries.length === 0 && (
        <div className="lv-empty">No entries.</div>
      )}

      {entries.length > 0 && (
        <>
          <div className="lv-count">
            {entries.length} {entries.length === 1 ? 'entry' : 'entries'}
            {data.deployment && <span className="lv-meta"> · deployment {data.deployment.slice(0, 12)}…</span>}
            {data.repo && <span className="lv-meta"> · {data.repo}</span>}
          </div>
          <ul className="lv-list">
            {entries.map((e, i) => (
              <li key={(e.rowId || e.runNumber || i) + ':' + i} className={`lv-row lv-row--${toneOf(e)}`}>
                <span className="lv-time" title={e.timestamp || ''}>{relativeTime(e.timestamp)}</span>

                {source === 'github' ? (
                  <span className="lv-body">
                    <span className="lv-name">{e.name}</span>
                    <span className="lv-tag">{e.conclusion}</span>
                    <span className="lv-dim">
                      {e.event}{e.runNumber ? ` · #${e.runNumber}` : ''}
                      {e.durationMs ? ` · ${fmtDuration(e.durationMs)}` : ''}
                    </span>
                    {e.url && <a className="lv-link" href={e.url} target="_blank" rel="noreferrer">open</a>}
                  </span>
                ) : source === 'activity' ? (
                  <span className="lv-body">
                    <span className="lv-name">{e.label}</span>
                    {e.tenantId && <span className="lv-tag" title={e.tenantId}>{e.tenantId.slice(0, 8)}</span>}
                    <span className="lv-msg">{e.detail}</span>
                  </span>
                ) : (
                  <span className="lv-body">
                    {e.status != null && <span className="lv-tag">{e.status}</span>}
                    {e.path && <span className="lv-dim">{e.method} {e.path}</span>}
                    <span className="lv-msg">{e.message}{e.truncated ? ' …' : ''}</span>
                  </span>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
